import React from 'react';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { NavLink } from '../types';
import logoImage from '../assets/airy.webp';
import { colors, typography, borderRadius, layout } from '../styles/designTokens';
import { useScrollAnimation } from '../utils/useScrollAnimation';

const PaymentPage = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [formData, setFormData] = React.useState({
    firstName: '',
    lastName: '',
    phone: '',
    street: '',
    buildingNumber: '',
    entrance: '',
    apartment: '',
    city: ''
  });
  const [termsAccepted, setTermsAccepted] = React.useState(false);
  const [errors, setErrors] = React.useState<{[key: string]: string}>({});
  const [touched, setTouched] = React.useState<{[key: string]: boolean}>({});
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Scroll animation for form
  const formWrapperRef = useScrollAnimation({ animationType: 'fadeIn', threshold: 0.1, triggerOnce: true });
  const [isFormVisible, setIsFormVisible] = React.useState(false);
  const formVisibleRef = React.useRef<HTMLDivElement>(null);

  // IntersectionObserver for Staggered Fade-In
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsFormVisible(true);
        }
      },
      {
        threshold: 0.2,
        rootMargin: '0px',
      }
    );

    const currentRef = formVisibleRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const navLinks: NavLink[] = [
    { label: 'בית', href: '/', isActive: false },
    { label: 'ממליצים', href: '/#testimonials', isActive: false },
    { label: 'מפרט המכונה', href: '/#product-specs', isActive: false },
    { label: 'נקודות מכירה', href: '/#branches', isActive: false },
    { label: 'צור קשר', href: '/#contact', isActive: false },
  ];

  // Validation functions
  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'firstName':
        if (!value.trim()) return 'שדה חובה';
        if (value.trim().length < 2) return 'שם פרטי חייב להכיל לפחות 2 תווים';
        return '';
      case 'lastName':
        if (!value.trim()) return 'שדה חובה';
        if (value.trim().length < 2) return 'שם משפחה חייב להכיל לפחות 2 תווים';
        return '';
      case 'phone':
        if (!value.trim()) return 'שדה חובה';
        const phoneRegex = /^[0-9]{9,10}$/;
        const cleanPhone = value.replace(/[\s-]/g, '');
        if (!phoneRegex.test(cleanPhone)) return 'מספר טלפון לא תקין (9-10 ספרות)';
        return '';
      case 'street':
        if (!value.trim()) return 'שדה חובה';
        if (value.trim().length < 2) return 'שם רחוב חייב להכיל לפחות 2 תווים';
        return '';
      case 'buildingNumber':
        if (!value.trim()) return 'שדה חובה';
        return '';
      case 'entrance':
        if (!value.trim()) return 'שדה חובה';
        return '';
      case 'apartment':
        if (!value.trim()) return 'שדה חובה';
        return '';
      case 'city':
        if (!value.trim()) return 'שדה חובה';
        if (value.trim().length < 2) return 'שם עיר חייב להכיל לפחות 2 תווים';
        return '';
      default:
        return '';
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name] && touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Mark all fields as touched
    const allTouched: {[key: string]: boolean} = {};
    Object.keys(formData).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);
    
    // Validate all fields
    const newErrors: {[key: string]: string} = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key as keyof typeof formData]);
      if (error) {
        newErrors[key] = error;
      }
    });
    
    // Check terms
    if (!termsAccepted) {
      setSnackbar({
        open: true,
        message: 'אנא אשר את תנאי השימוש',
        severity: 'warning',
      });
      return;
    }
    
    setErrors(newErrors);
    
    // If there are errors, don't submit
    if (Object.keys(newErrors).length > 0) {
      setSnackbar({
        open: true,
        message: 'אנא מלא את כל השדות הנדרשים',
        severity: 'warning',
      });
      return;
    }
    
    try {
      localStorage.setItem('orderData', JSON.stringify(formData));
      
      const isTestMode = process.env.REACT_APP_TEST_MODE === 'true';
      
      if (isTestMode) {
        window.location.href = '/payment-success?response=success&transaction_id=TEST_' + Date.now();
      } else {
        const growPaymentUrl = process.env.REACT_APP_GROW_PAYMENT_URL || 'https://pay.grow.link/87a0163918b12b73d8f27c347b33e284-MjY2NzA4Nw';
        // 🔧 FIX: שינוי מ-window.open ל-window.location.href
        // סיבה: כך הנתונים נשמרים ב-localStorage באותו טאב ונשלחים ל-Google Sheets + מייל
        window.location.href = growPaymentUrl;
      }
      
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'אירעה שגיאה בשליחת ההזמנה. אנא נסה שוב או צור קשר בטלפון.',
        severity: 'error',
      });
    }
  };

  return (
    <div
      className="min-h-screen scroll-smooth"
      style={{
        fontFamily: typography.fontFamily,
        backgroundColor: '#000000',
      }}
    >
      {/* Skip to main content link */}
      <a
        href="#main-content"
        style={{
          position: 'absolute',
          left: '-9999px',
          zIndex: 999,
          padding: '12px 24px',
          backgroundColor: colors.orangeGold,
          color: '#000000',
          fontWeight: 700,
          fontSize: '1rem',
          textDecoration: 'none',
          borderRadius: '4px',
        }}
        onFocus={(e) => {
          e.currentTarget.style.left = '50%';
          e.currentTarget.style.top = '10px';
          e.currentTarget.style.transform = 'translateX(-50%)';
        }}
        onBlur={(e) => {
          e.currentTarget.style.left = '-9999px';
          e.currentTarget.style.top = 'auto';
          e.currentTarget.style.transform = 'none';
        }}
      >
        דלג לתוכן הראשי
      </a>

      <Header
        logoImage={logoImage}
        navLinks={navLinks}
        isMobileMenuOpen={isMobileMenuOpen}
        onMobileMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />

      {/* Main Content */}
      <main id="main-content" className="py-8 md:py-12" style={{ 
        paddingTop: 'clamp(60px, calc(5vw + 70px), 110px)', // padding-top גדול כדי שהכותרת לא תהיה מוסתרת על ידי ה-navbar
        paddingBottom: '40px',
      }}>
        <div className="container mx-auto px-4" style={{ maxWidth: layout.contentMaxWidth }}>
          <div className="max-w-5xl mx-auto">
            {/* Back Button - כפתור חזרה לדף הבית */}
            <div style={{ marginBottom: 'clamp(16px, 2.5vw, 24px)' }}>
              <a
                href="/"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: colors.orangeGold,
                  fontSize: 'clamp(0.95rem, 1.8vw, 1.1rem)',
                  fontWeight: 600,
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  padding: '8px 12px',
                  borderRadius: '8px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = colors.gold.mediumLight;
                  e.currentTarget.style.background = 'rgba(245, 182, 88, 0.1)';
                  e.currentTarget.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = colors.orangeGold;
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <span style={{ fontSize: '1.2em', lineHeight: '1' }}>→</span>
                <span>חזרה לדף הבית</span>
              </a>
            </div>
            
            {/* Page Title */}
            <h1 
              className="text-center mb-6 md:mb-8"
              style={{ 
                color: colors.orangeGold,
                fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                fontWeight: 900,
                lineHeight: 1.2,
                letterSpacing: typography.heading.letterSpacing,
                scrollMarginTop: '80px', // מבטיח שהכותרת תראה כשגוללים לכאן
              }}
            >
              תשלום
            </h1>
            
            <div className="max-w-2xl mx-auto">
              {/* Payment Form - השדות קודם, אחר כך סיכום הזמנה */}
              <div 
                ref={formWrapperRef}
                className="p-4 md:p-5 rounded-xl"
                style={{ 
                  backgroundColor: '#000000',
                  border: '2px solid #FFFFFF',
                  borderRadius: borderRadius.soft,
                }}
              >
                <div ref={formVisibleRef}>
                  <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                    {/* פרטים אישיים */}
                    <div>
                      <h2 
                        className="mb-4"
                        style={{ 
                          color: colors.orangeGold,
                          fontSize: 'clamp(1.125rem, 2vw, 1.25rem)',
                          fontWeight: 700,
                        }}
                      >
                        פרטים אישיים
                      </h2>
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <TextField
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          placeholder="שם פרטי"
                          fullWidth
                          variant="outlined"
                          required
                          aria-label="שם פרטי"
                          error={!!(touched.firstName && errors.firstName)}
                          helperText={touched.firstName && errors.firstName ? errors.firstName : ' '}
                          InputLabelProps={{
                            shrink: false,
                          }}
                          FormHelperTextProps={{
                            sx: {
                              marginRight: 0,
                              marginLeft: 0,
                              marginTop: '4px',
                              fontSize: '0.75rem',
                              color: 'rgba(245, 182, 88, 0.9) !important',
                              minHeight: '20px',
                              display: 'block',
                            }
                          }}
                          sx={{
                            opacity: isFormVisible ? 1 : 0,
                            transform: isFormVisible ? 'translateY(0)' : 'translateY(20px)',
                            transition: 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s',
                            '& .MuiFormHelperText-root': {
                              color: 'rgba(245, 182, 88, 0.9) !important',
                              minHeight: '20px',
                              display: 'block !important',
                            },
                            '& .MuiFormHelperText-root.Mui-error': {
                              color: 'rgba(245, 182, 88, 0.9) !important',
                            },
                            '& .MuiOutlinedInput-root': {
                              fontSize: typography.fontSize.base,
                              backgroundColor: '#000000 !important',
                              color: '#FFFFFF !important',
                              '& input': {
                                textAlign: 'right',
                                padding: '16px 14px',
                                color: '#FFFFFF !important',
                                backgroundColor: 'transparent !important',
                              },
                              '& input:-webkit-autofill': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '& input:-webkit-autofill:hover': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '& input:-webkit-autofill:focus': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '& input:-webkit-autofill:active': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '&.Mui-focused': {
                                backgroundColor: '#000000 !important',
                              },
                              '&:hover': {
                                backgroundColor: '#000000 !important',
                              },
                              '&.Mui-filled': {
                                backgroundColor: '#000000 !important',
                              },
                              '& fieldset': {
                                borderColor: (touched.firstName && errors.firstName) 
                                  ? 'rgba(245, 182, 88, 0.6)' 
                                  : (formData.firstName ? colors.gold.base : 'rgba(245, 182, 88, 0.3)'),
                                transition: 'border-color 0.3s ease',
                              },
                              '&.Mui-error fieldset': {
                                borderColor: 'rgba(245, 182, 88, 0.6)',
                              },
                              '&:hover fieldset': {
                                borderColor: (touched.firstName && errors.firstName) 
                                  ? 'rgba(245, 182, 88, 0.8)' 
                                  : colors.gold.base,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: (touched.firstName && errors.firstName) 
                                  ? 'rgba(245, 182, 88, 0.8)' 
                                  : colors.gold.base,
                                borderWidth: '3px',
                                boxShadow: (touched.firstName && errors.firstName)
                                  ? `0 0 0 4px ${colors.gold.mediumLight}20, 0 0 20px ${colors.gold.mediumLight}50`
                                  : `0 0 0 4px ${colors.gold.mediumLight}30, 0 0 20px ${colors.gold.mediumLight}60`,
                                transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              display: 'none',
                            },
                            '& input::placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                            '& input::-webkit-input-placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                            '& input::-moz-placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                            '& input:-ms-input-placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                          }}
                        />
                        <TextField
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          placeholder="שם משפחה"
                          fullWidth
                          variant="outlined"
                          required
                          aria-label="שם משפחה"
                          error={!!(touched.lastName && errors.lastName)}
                          helperText={touched.lastName && errors.lastName ? errors.lastName : ' '}
                          InputLabelProps={{
                            shrink: false,
                          }}
                          FormHelperTextProps={{
                            sx: {
                              marginRight: 0,
                              marginLeft: 0,
                              marginTop: '4px',
                              fontSize: '0.75rem',
                              color: 'rgba(245, 182, 88, 0.9) !important',
                              minHeight: '20px',
                              display: 'block',
                            }
                          }}
                          sx={{
                            opacity: isFormVisible ? 1 : 0,
                            transform: isFormVisible ? 'translateY(0)' : 'translateY(20px)',
                            transition: 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s',
                            '& .MuiFormHelperText-root': {
                              color: 'rgba(245, 182, 88, 0.9) !important',
                              minHeight: '20px',
                              display: 'block !important',
                            },
                            '& .MuiFormHelperText-root.Mui-error': {
                              color: 'rgba(245, 182, 88, 0.9) !important',
                            },
                            '& .MuiOutlinedInput-root': {
                              fontSize: typography.fontSize.base,
                              backgroundColor: '#000000 !important',
                              color: '#FFFFFF !important',
                              '& input': {
                                textAlign: 'right',
                                padding: '16px 14px',
                                color: '#FFFFFF !important',
                                backgroundColor: 'transparent !important',
                              },
                              '& input:-webkit-autofill': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '& input:-webkit-autofill:hover': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '& input:-webkit-autofill:focus': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '& input:-webkit-autofill:active': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '&.Mui-focused': {
                                backgroundColor: '#000000 !important',
                              },
                              '&:hover': {
                                backgroundColor: '#000000 !important',
                              },
                              '&.Mui-filled': {
                                backgroundColor: '#000000 !important',
                              },
                              '& fieldset': {
                                borderColor: (touched.lastName && errors.lastName) 
                                  ? 'rgba(245, 182, 88, 0.6)' 
                                  : (formData.lastName ? colors.gold.base : 'rgba(245, 182, 88, 0.3)'),
                                transition: 'border-color 0.3s ease',
                              },
                              '&.Mui-error fieldset': {
                                borderColor: 'rgba(245, 182, 88, 0.6)',
                              },
                              '&:hover fieldset': {
                                borderColor: (touched.lastName && errors.lastName) 
                                  ? 'rgba(245, 182, 88, 0.8)' 
                                  : colors.gold.base,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: (touched.lastName && errors.lastName) 
                                  ? 'rgba(245, 182, 88, 0.8)' 
                                  : colors.gold.base,
                                borderWidth: '3px',
                                boxShadow: (touched.lastName && errors.lastName)
                                  ? `0 0 0 4px ${colors.gold.mediumLight}20, 0 0 20px ${colors.gold.mediumLight}50`
                                  : `0 0 0 4px ${colors.gold.mediumLight}30, 0 0 20px ${colors.gold.mediumLight}60`,
                                transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              display: 'none',
                            },
                            '& input::placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                            '& input::-webkit-input-placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                            '& input::-moz-placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                            '& input:-ms-input-placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                          }}
                        />
                      </div>
                      <TextField
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="טלפון"
                        fullWidth
                        variant="outlined"
                        required
                        aria-label="מספר טלפון"
                        error={!!(touched.phone && errors.phone)}
                        helperText={touched.phone && errors.phone ? errors.phone : ' '}
                        InputLabelProps={{
                          shrink: false,
                        }}
                        FormHelperTextProps={{
                          sx: {
                            marginRight: 0,
                            marginLeft: 0,
                            marginTop: '4px',
                            fontSize: '0.75rem',
                            color: 'rgba(245, 182, 88, 0.9) !important',
                            minHeight: '20px',
                            display: 'block',
                          }
                        }}
                        sx={{
                          opacity: isFormVisible ? 1 : 0,
                          transform: isFormVisible ? 'translateY(0)' : 'translateY(20px)',
                          transition: 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s',
                          '& .MuiFormHelperText-root': {
                            color: 'rgba(245, 182, 88, 0.9) !important',
                            minHeight: '20px',
                            display: 'block !important',
                          },
                          '& .MuiFormHelperText-root.Mui-error': {
                            color: 'rgba(245, 182, 88, 0.9) !important',
                          },
                          '& .MuiOutlinedInput-root': {
                            fontSize: typography.fontSize.base,
                            backgroundColor: '#000000 !important',
                            color: '#FFFFFF !important',
                            '& input': {
                              textAlign: 'right',
                              padding: '16px 14px',
                              color: '#FFFFFF !important',
                              backgroundColor: 'transparent !important',
                            },
                            '& input:-webkit-autofill': {
                              WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                              WebkitTextFillColor: '#FFFFFF !important',
                              backgroundColor: '#000000 !important',
                            },
                            '& input:-webkit-autofill:hover': {
                              WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                              WebkitTextFillColor: '#FFFFFF !important',
                              backgroundColor: '#000000 !important',
                            },
                            '& input:-webkit-autofill:focus': {
                              WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                              WebkitTextFillColor: '#FFFFFF !important',
                              backgroundColor: '#000000 !important',
                            },
                            '& input:-webkit-autofill:active': {
                              WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                              WebkitTextFillColor: '#FFFFFF !important',
                              backgroundColor: '#000000 !important',
                            },
                            '&.Mui-focused': {
                              backgroundColor: '#000000 !important',
                            },
                            '&:hover': {
                              backgroundColor: '#000000 !important',
                            },
                            '&.Mui-filled': {
                              backgroundColor: '#000000 !important',
                            },
                            '& fieldset': {
                              borderColor: (touched.phone && errors.phone) 
                                ? 'rgba(245, 182, 88, 0.6)' 
                                : (formData.phone ? colors.gold.base : 'rgba(245, 182, 88, 0.3)'),
                              transition: 'border-color 0.3s ease',
                            },
                            '&.Mui-error fieldset': {
                              borderColor: 'rgba(245, 182, 88, 0.6)',
                            },
                            '&:hover fieldset': {
                              borderColor: (touched.phone && errors.phone) 
                                ? 'rgba(245, 182, 88, 0.8)' 
                                : colors.gold.base,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: (touched.phone && errors.phone) 
                                ? 'rgba(245, 182, 88, 0.8)' 
                                : colors.gold.base,
                              borderWidth: '3px',
                              boxShadow: (touched.phone && errors.phone)
                                ? `0 0 0 4px ${colors.gold.mediumLight}20, 0 0 20px ${colors.gold.mediumLight}50`
                                : `0 0 0 4px ${colors.gold.mediumLight}30, 0 0 20px ${colors.gold.mediumLight}60`,
                              transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            display: 'none',
                          },
                          '& input::placeholder': {
                            color: '#FFFFFF !important',
                            opacity: 1,
                          },
                          '& input::-webkit-input-placeholder': {
                            color: '#FFFFFF !important',
                            opacity: 1,
                          },
                          '& input::-moz-placeholder': {
                            color: '#FFFFFF !important',
                            opacity: 1,
                          },
                          '& input:-ms-input-placeholder': {
                            color: '#FFFFFF !important',
                            opacity: 1,
                          },
                        }}
                      />
                    </div>

                    {/* כתובת */}
                    <div>
                      <h2 
                        className="mb-4"
                        style={{ 
                          color: colors.orangeGold,
                          fontSize: 'clamp(1.125rem, 2vw, 1.25rem)',
                          fontWeight: 700,
                        }}
                      >
                        כתובת
                      </h2>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: layout.gap.medium }}>
                      <TextField
                        id="street"
                        name="street"
                        value={formData.street}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="רחוב"
                        fullWidth
                        variant="outlined"
                        required
                        aria-label="שם רחוב"
                        error={!!(touched.street && errors.street)}
                        helperText={touched.street && errors.street ? errors.street : ' '}
                        InputLabelProps={{
                          shrink: false,
                        }}
                        FormHelperTextProps={{
                          sx: {
                            marginRight: 0,
                            marginLeft: 0,
                            marginTop: '4px',
                            fontSize: '0.75rem',
                            color: 'rgba(245, 182, 88, 0.9) !important',
                            minHeight: '20px',
                            display: 'block',
                          }
                        }}
                        sx={{
                          opacity: isFormVisible ? 1 : 0,
                          transform: isFormVisible ? 'translateY(0)' : 'translateY(20px)',
                          transition: 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s',
                          '& .MuiFormHelperText-root': {
                            color: 'rgba(245, 182, 88, 0.9) !important',
                            minHeight: '20px',
                            display: 'block !important',
                          },
                          '& .MuiFormHelperText-root.Mui-error': {
                            color: 'rgba(245, 182, 88, 0.9) !important',
                          },
                          '& .MuiOutlinedInput-root': {
                            fontSize: typography.fontSize.base,
                            backgroundColor: '#000000 !important',
                            color: '#FFFFFF !important',
                            '& input': {
                              textAlign: 'right',
                              padding: '16px 14px',
                              color: '#FFFFFF !important',
                              backgroundColor: 'transparent !important',
                            },
                            '& input:-webkit-autofill': {
                              WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                              WebkitTextFillColor: '#FFFFFF !important',
                              backgroundColor: '#000000 !important',
                            },
                            '& input:-webkit-autofill:hover': {
                              WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                              WebkitTextFillColor: '#FFFFFF !important',
                              backgroundColor: '#000000 !important',
                            },
                            '& input:-webkit-autofill:focus': {
                              WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                              WebkitTextFillColor: '#FFFFFF !important',
                              backgroundColor: '#000000 !important',
                            },
                            '& input:-webkit-autofill:active': {
                              WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                              WebkitTextFillColor: '#FFFFFF !important',
                              backgroundColor: '#000000 !important',
                            },
                            '&.Mui-focused': {
                              backgroundColor: '#000000 !important',
                            },
                            '&:hover': {
                              backgroundColor: '#000000 !important',
                            },
                            '&.Mui-filled': {
                              backgroundColor: '#000000 !important',
                            },
                            '& fieldset': {
                              borderColor: (touched.street && errors.street) 
                                ? 'rgba(245, 182, 88, 0.6)' 
                                : (formData.street ? colors.gold.base : 'rgba(245, 182, 88, 0.3)'),
                              transition: 'border-color 0.3s ease',
                            },
                            '&.Mui-error fieldset': {
                              borderColor: 'rgba(245, 182, 88, 0.6)',
                            },
                            '&:hover fieldset': {
                              borderColor: (touched.street && errors.street) 
                                ? 'rgba(245, 182, 88, 0.8)' 
                                : colors.gold.base,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: (touched.street && errors.street) 
                                ? 'rgba(245, 182, 88, 0.8)' 
                                : colors.gold.base,
                              borderWidth: '3px',
                              boxShadow: (touched.street && errors.street)
                                ? `0 0 0 4px ${colors.gold.mediumLight}20, 0 0 20px ${colors.gold.mediumLight}50`
                                : `0 0 0 4px ${colors.gold.mediumLight}30, 0 0 20px ${colors.gold.mediumLight}60`,
                              transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            display: 'none',
                          },
                          '& input::placeholder': {
                            color: '#FFFFFF !important',
                            opacity: 1,
                          },
                          '& input::-webkit-input-placeholder': {
                            color: '#FFFFFF !important',
                            opacity: 1,
                          },
                          '& input::-moz-placeholder': {
                            color: '#FFFFFF !important',
                            opacity: 1,
                          },
                          '& input:-ms-input-placeholder': {
                            color: '#FFFFFF !important',
                            opacity: 1,
                          },
                        }}
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <TextField
                          id="buildingNumber"
                          name="buildingNumber"
                          value={formData.buildingNumber}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          placeholder="מספר בנין"
                          variant="outlined"
                          required
                          aria-label="מספר בנין"
                          error={!!(touched.buildingNumber && errors.buildingNumber)}
                          helperText={touched.buildingNumber && errors.buildingNumber ? errors.buildingNumber : ' '}
                          InputLabelProps={{
                            shrink: false,
                          }}
                          FormHelperTextProps={{
                            sx: {
                              marginRight: 0,
                              marginLeft: 0,
                              marginTop: '4px',
                              fontSize: '0.75rem',
                              color: 'rgba(245, 182, 88, 0.9) !important',
                              minHeight: '20px',
                              display: 'block',
                            }
                          }}
                          sx={{
                            opacity: isFormVisible ? 1 : 0,
                            transform: isFormVisible ? 'translateY(0)' : 'translateY(20px)',
                            transition: 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s',
                            '& .MuiFormHelperText-root': {
                              color: 'rgba(245, 182, 88, 0.9) !important',
                              minHeight: '20px',
                              display: 'block !important',
                            },
                            '& .MuiFormHelperText-root.Mui-error': {
                              color: 'rgba(245, 182, 88, 0.9) !important',
                            },
                          '& .MuiOutlinedInput-root': {
                              fontSize: typography.fontSize.base,
                              backgroundColor: '#000000 !important',
                              color: '#FFFFFF !important',
                              '& input': {
                                textAlign: 'right',
                                padding: '16px 14px',
                                color: '#FFFFFF !important',
                                backgroundColor: 'transparent !important',
                              },
                              '& input:-webkit-autofill': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '& input:-webkit-autofill:hover': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '& input:-webkit-autofill:focus': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '& input:-webkit-autofill:active': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '&.Mui-focused': {
                                backgroundColor: '#000000 !important',
                              },
                              '&:hover': {
                                backgroundColor: '#000000 !important',
                              },
                              '&.Mui-filled': {
                                backgroundColor: '#000000 !important',
                              },
                              '& fieldset': {
                                borderColor: (touched.buildingNumber && errors.buildingNumber) 
                                  ? 'rgba(245, 182, 88, 0.6)' 
                                  : 'rgba(245, 182, 88, 0.3)',
                                transition: 'border-color 0.3s ease',
                              },
                              '&.Mui-error fieldset': {
                                borderColor: 'rgba(245, 182, 88, 0.6)',
                              },
                              '&:hover fieldset': {
                                borderColor: (touched.buildingNumber && errors.buildingNumber) 
                                  ? 'rgba(245, 182, 88, 0.8)' 
                                  : colors.gold.base,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: (touched.buildingNumber && errors.buildingNumber) 
                                  ? 'rgba(245, 182, 88, 0.8)' 
                                  : colors.gold.base,
                                borderWidth: '3px',
                                boxShadow: (touched.buildingNumber && errors.buildingNumber)
                                  ? `0 0 0 4px ${colors.gold.mediumLight}20, 0 0 20px ${colors.gold.mediumLight}50`
                                  : `0 0 0 4px ${colors.gold.mediumLight}30, 0 0 20px ${colors.gold.mediumLight}60`,
                                transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                              },
                              '& .MuiInputBase-input': {
                                color: '#FFFFFF !important',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              display: 'none',
                            },
                            '& input::placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                            '& input::-webkit-input-placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                            '& input::-moz-placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                            '& input:-ms-input-placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                          }}
                        />
                        <TextField
                          id="entrance"
                          name="entrance"
                          value={formData.entrance}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          placeholder="כניסה"
                          variant="outlined"
                          required
                          aria-label="מספר כניסה"
                          error={!!(touched.entrance && errors.entrance)}
                          helperText={touched.entrance && errors.entrance ? errors.entrance : ' '}
                          InputLabelProps={{
                            shrink: false,
                          }}
                          FormHelperTextProps={{
                            sx: {
                              marginRight: 0,
                              marginLeft: 0,
                              marginTop: '4px',
                              fontSize: '0.75rem',
                              color: 'rgba(245, 182, 88, 0.9) !important',
                              minHeight: '20px',
                              display: 'block',
                            }
                          }}
                          sx={{
                            opacity: isFormVisible ? 1 : 0,
                            transform: isFormVisible ? 'translateY(0)' : 'translateY(20px)',
                            transition: 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.55s, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.55s',
                            '& .MuiFormHelperText-root': {
                              color: 'rgba(245, 182, 88, 0.9) !important',
                              minHeight: '20px',
                              display: 'block !important',
                            },
                            '& .MuiFormHelperText-root.Mui-error': {
                              color: 'rgba(245, 182, 88, 0.9) !important',
                            },
                            '& .MuiOutlinedInput-root': {
                              fontSize: typography.fontSize.base,
                              backgroundColor: '#000000 !important',
                              color: '#FFFFFF !important',
                              '& input': {
                                textAlign: 'right',
                                padding: '16px 14px',
                                color: '#FFFFFF !important',
                                backgroundColor: 'transparent !important',
                              },
                              '& input:-webkit-autofill': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '& input:-webkit-autofill:hover': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '& input:-webkit-autofill:focus': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '& input:-webkit-autofill:active': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '&.Mui-focused': {
                                backgroundColor: '#000000 !important',
                              },
                              '&:hover': {
                                backgroundColor: '#000000 !important',
                              },
                              '&.Mui-filled': {
                                backgroundColor: '#000000 !important',
                              },
                              '& fieldset': {
                                borderColor: (touched.entrance && errors.entrance) 
                                  ? 'rgba(245, 182, 88, 0.6)' 
                                  : 'rgba(245, 182, 88, 0.3)',
                                transition: 'border-color 0.3s ease',
                              },
                              '&.Mui-error fieldset': {
                                borderColor: 'rgba(245, 182, 88, 0.6)',
                              },
                              '&:hover fieldset': {
                                borderColor: (touched.entrance && errors.entrance) 
                                  ? 'rgba(245, 182, 88, 0.8)' 
                                  : colors.gold.base,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: (touched.entrance && errors.entrance) 
                                  ? 'rgba(245, 182, 88, 0.8)' 
                                  : colors.gold.base,
                                borderWidth: '3px',
                                boxShadow: (touched.entrance && errors.entrance)
                                  ? `0 0 0 4px ${colors.gold.mediumLight}20, 0 0 20px ${colors.gold.mediumLight}50`
                                  : `0 0 0 4px ${colors.gold.mediumLight}30, 0 0 20px ${colors.gold.mediumLight}60`,
                                transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                              },
                              '& .MuiInputBase-input': {
                                color: '#FFFFFF !important',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              display: 'none',
                            },
                            '& input::placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                            '& input::-webkit-input-placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                            '& input::-moz-placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                            '& input:-ms-input-placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                          }}
                        />
                        <TextField
                          id="apartment"
                          name="apartment"
                          value={formData.apartment}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          placeholder="דירה"
                          variant="outlined"
                          required
                          aria-label="מספר דירה"
                          error={!!(touched.apartment && errors.apartment)}
                          helperText={touched.apartment && errors.apartment ? errors.apartment : ' '}
                          InputLabelProps={{
                            shrink: false,
                          }}
                          FormHelperTextProps={{
                            sx: {
                              marginRight: 0,
                              marginLeft: 0,
                              marginTop: '4px',
                              fontSize: '0.75rem',
                              color: 'rgba(245, 182, 88, 0.9) !important',
                              minHeight: '20px',
                              display: 'block',
                            }
                          }}
                          sx={{
                            opacity: isFormVisible ? 1 : 0,
                            transform: isFormVisible ? 'translateY(0)' : 'translateY(20px)',
                            transition: 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.6s, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.6s',
                            '& .MuiFormHelperText-root': {
                              color: 'rgba(245, 182, 88, 0.9) !important',
                              minHeight: '20px',
                              display: 'block !important',
                            },
                            '& .MuiFormHelperText-root.Mui-error': {
                              color: 'rgba(245, 182, 88, 0.9) !important',
                            },
                            '& .MuiOutlinedInput-root': {
                              fontSize: typography.fontSize.base,
                              backgroundColor: '#000000 !important',
                              color: '#FFFFFF !important',
                              '& input': {
                                textAlign: 'right',
                                padding: '16px 14px',
                                color: '#FFFFFF !important',
                                backgroundColor: 'transparent !important',
                              },
                              '& input:-webkit-autofill': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '& input:-webkit-autofill:hover': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '& input:-webkit-autofill:focus': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '& input:-webkit-autofill:active': {
                                WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                                WebkitTextFillColor: '#FFFFFF !important',
                                backgroundColor: '#000000 !important',
                              },
                              '&.Mui-focused': {
                                backgroundColor: '#000000 !important',
                              },
                              '&:hover': {
                                backgroundColor: '#000000 !important',
                              },
                              '&.Mui-filled': {
                                backgroundColor: '#000000 !important',
                              },
                              '& fieldset': {
                                borderColor: (touched.apartment && errors.apartment) 
                                  ? 'rgba(245, 182, 88, 0.6)' 
                                  : 'rgba(245, 182, 88, 0.3)',
                                transition: 'border-color 0.3s ease',
                              },
                              '&.Mui-error fieldset': {
                                borderColor: 'rgba(245, 182, 88, 0.6)',
                              },
                              '&:hover fieldset': {
                                borderColor: (touched.apartment && errors.apartment) 
                                  ? 'rgba(245, 182, 88, 0.8)' 
                                  : colors.gold.base,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: (touched.apartment && errors.apartment) 
                                  ? 'rgba(245, 182, 88, 0.8)' 
                                  : colors.gold.base,
                                borderWidth: '3px',
                                boxShadow: (touched.apartment && errors.apartment)
                                  ? `0 0 0 4px ${colors.gold.mediumLight}20, 0 0 20px ${colors.gold.mediumLight}50`
                                  : `0 0 0 4px ${colors.gold.mediumLight}30, 0 0 20px ${colors.gold.mediumLight}60`,
                                transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                              },
                              '& .MuiInputBase-input': {
                                color: '#FFFFFF !important',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              display: 'none',
                            },
                            '& input::placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                            '& input::-webkit-input-placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                            '& input::-moz-placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                            '& input:-ms-input-placeholder': {
                              color: '#FFFFFF !important',
                              opacity: 1,
                            },
                          }}
                        />
                      </div>
                      <TextField
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="עיר"
                        fullWidth
                        variant="outlined"
                        required
                        aria-label="שם עיר"
                        error={!!(touched.city && errors.city)}
                        helperText={touched.city && errors.city ? errors.city : ' '}
                        InputLabelProps={{
                          shrink: false,
                        }}
                        FormHelperTextProps={{
                          sx: {
                            marginRight: 0,
                            marginLeft: 0,
                            marginTop: '4px',
                            fontSize: '0.75rem',
                            color: 'rgba(245, 182, 88, 0.9) !important',
                            minHeight: '20px',
                            display: 'block',
                          }
                        }}
                        sx={{
                          opacity: isFormVisible ? 1 : 0,
                          transform: isFormVisible ? 'translateY(0)' : 'translateY(20px)',
                          transition: 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.65s, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.65s',
                          '& .MuiFormHelperText-root': {
                            color: 'rgba(245, 182, 88, 0.9) !important',
                            minHeight: '20px',
                            display: 'block !important',
                          },
                          '& .MuiFormHelperText-root.Mui-error': {
                            color: 'rgba(245, 182, 88, 0.9) !important',
                          },
                          '& .MuiOutlinedInput-root': {
                            fontSize: typography.fontSize.base,
                            backgroundColor: '#000000 !important',
                            color: '#FFFFFF !important',
                            '& input': {
                              textAlign: 'right',
                              padding: '16px 14px',
                              color: '#FFFFFF !important',
                              backgroundColor: 'transparent !important',
                            },
                            '& input:-webkit-autofill': {
                              WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                              WebkitTextFillColor: '#FFFFFF !important',
                              backgroundColor: '#000000 !important',
                            },
                            '& input:-webkit-autofill:hover': {
                              WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                              WebkitTextFillColor: '#FFFFFF !important',
                              backgroundColor: '#000000 !important',
                            },
                            '& input:-webkit-autofill:focus': {
                              WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                              WebkitTextFillColor: '#FFFFFF !important',
                              backgroundColor: '#000000 !important',
                            },
                            '& input:-webkit-autofill:active': {
                              WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                              WebkitTextFillColor: '#FFFFFF !important',
                              backgroundColor: '#000000 !important',
                            },
                            '&.Mui-focused': {
                              backgroundColor: '#000000 !important',
                            },
                            '&:hover': {
                              backgroundColor: '#000000 !important',
                            },
                            '&.Mui-filled': {
                              backgroundColor: '#000000 !important',
                            },
                            '& fieldset': {
                              borderColor: (touched.city && errors.city) 
                                ? 'rgba(245, 182, 88, 0.6)' 
                                : (formData.city ? colors.gold.base : 'rgba(245, 182, 88, 0.3)'),
                              transition: 'border-color 0.3s ease',
                            },
                            '&.Mui-error fieldset': {
                              borderColor: 'rgba(245, 182, 88, 0.6)',
                            },
                            '&:hover fieldset': {
                              borderColor: (touched.city && errors.city) 
                                ? 'rgba(245, 182, 88, 0.8)' 
                                : colors.gold.base,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: (touched.city && errors.city) 
                                ? 'rgba(245, 182, 88, 0.8)' 
                                : colors.gold.base,
                              borderWidth: '3px',
                              boxShadow: (touched.city && errors.city)
                                ? `0 0 0 4px ${colors.gold.mediumLight}20, 0 0 20px ${colors.gold.mediumLight}50`
                                : `0 0 0 4px ${colors.gold.mediumLight}30, 0 0 20px ${colors.gold.mediumLight}60`,
                              transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            display: 'none',
                          },
                          '& input::placeholder': {
                            color: '#FFFFFF !important',
                            opacity: 1,
                          },
                          '& input::-webkit-input-placeholder': {
                            color: '#FFFFFF !important',
                            opacity: 1,
                          },
                          '& input::-moz-placeholder': {
                            color: '#FFFFFF !important',
                            opacity: 1,
                          },
                          '& input:-ms-input-placeholder': {
                            color: '#FFFFFF !important',
                            opacity: 1,
                          },
                        }}
                      />
                    </div>
                  </div>

                  {/* Order Summary - אחרי השדות, לפני הצ'קבוקס והכפתור */}
                  <div style={{ 
                    opacity: isFormVisible ? 1 : 0,
                    transform: isFormVisible ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.7s, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.7s',
                  }}>
                    <h2 
                      className="mb-4"
                      style={{ 
                        color: colors.orangeGold,
                        fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
                        fontWeight: 900,
                        lineHeight: 1.2,
                        letterSpacing: typography.heading.letterSpacing,
                      }}
                    >
                      סיכום הזמנה
                    </h2>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#FFFFFF', fontSize: typography.fontSize.base, fontWeight: 400 }}>
                          מכונת AiRY
                        </span>
                        <span style={{ color: colors.orangeGold, fontSize: typography.fontSize.base, fontWeight: 700 }}>
                          ₪389
                        </span>
                      </div>
                      
                      {/* Free Shipping Line - שורת משלוח חינם */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#FFFFFF', fontSize: typography.fontSize.base, fontWeight: 400 }}>
                          משלוח
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ 
                            color: colors.gold.mediumDark, 
                            fontSize: typography.fontSize.base, 
                            fontWeight: 700,
                            textDecoration: 'line-through',
                          }}>
                            ₪30
                          </span>
                          <span style={{ 
                            color: colors.orangeGold, 
                            fontSize: typography.fontSize.base, 
                            fontWeight: 700 
                          }}>
                            ₪0
                          </span>
                          <span style={{ 
                            color: colors.orangeGold, 
                            fontSize: typography.fontSize.sm, 
                            fontWeight: 700,
                            background: 'rgba(245, 182, 88, 0.15)',
                            padding: '2px 8px',
                            borderRadius: '4px',
                          }}>
                            חינם!
                          </span>
                        </div>
                      </div>
                      
                      <div style={{ 
                        borderTop: `1.5px solid ${colors.orangeGold}`, 
                        paddingTop: layout.gap.medium,
                        marginTop: layout.gap.small,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: colors.orangeGold, fontSize: typography.fontSize.lg, fontWeight: 900 }}>
                            סה"כ לתשלום
                          </span>
                          <span style={{ color: colors.orangeGold, fontSize: typography.fontSize.xl, fontWeight: 900 }}>
                            ₪389
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* תנאים והגבלות וכפתור תשלום - בתחתית התיבה */}
                  <div style={{ 
                    paddingTop: '24px', 
                    borderTop: `1.5px solid ${colors.orangeGold}`,
                    opacity: isFormVisible ? 1 : 0,
                    transform: isFormVisible ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.75s, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.75s',
                  }}>
                    {/* תנאים והגבלות */}
                    <div style={{ marginBottom: '16px' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            sx={{
                              color: colors.orangeGold,
                              '&.Mui-checked': {
                                color: colors.orange,
                              },
                              '& .MuiSvgIcon-root': {
                                fontSize: 20,
                              },
                            }}
                          />
                        }
                        label={
                          <span style={{ color: colors.gold.base, fontSize: typography.fontSize.sm }}>
                            אני מסכימ/ה ל{' '}
                            <a 
                              href="/website-terms" 
                              target="_blank"
                              className="underline"
                              style={{ color: colors.gold.mediumLight }}
                            >
                              תקנון אתר ומשלוחים
                            </a>
                          </span>
                        }
                        sx={{
                          alignItems: 'center',
                          marginRight: 0,
                          marginLeft: 'auto',
                          marginTop: 0,
                          marginBottom: 0,
                          '& .MuiFormControlLabel-label': {
                            marginRight: '8px',
                            marginTop: 0,
                            marginBottom: 0,
                          },
                        }}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={!termsAccepted}
                      className="group rounded-pill font-bold text-center transition-all duration-300 flex items-center justify-center gap-2 relative"
                      style={{
                        background: termsAccepted ? colors.accentSpecial.buttonGradientDiagonal : '#808080',
                        letterSpacing: typography.body.letterSpacing,
                        cursor: termsAccepted ? 'pointer' : 'not-allowed',
                        opacity: !termsAccepted ? 0.5 : (isFormVisible ? 1 : 0),
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                        transform: isFormVisible ? 'translateY(0)' : 'translateY(20px)',
                        transition: 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.8s, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.8s, background 0.3s ease',
                        animation: termsAccepted ? 'orderButtonColorSwitch 2s steps(1, end) infinite' : 'none',
                        padding: 'clamp(10px, 1.2vw, 12px) clamp(60px, 8vw, 120px)',
                        justifyContent: 'center',
                        color: '#000000',
                        fontSize: 'clamp(18px, 2.2vw, 22px)',
                        width: 'auto',
                        maxWidth: '400px',
                        margin: '0 auto',
                        overflow: 'visible',
                      }}
                    >
                      {/* Shimmer Effect */}
                      {termsAccepted && (
                        <span 
                          className="shimmer-effect absolute inset-0 opacity-0"
                          style={{
                            background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)',
                            animation: 'shimmer 3s infinite',
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                      <span>תשלום</span>
                      
                      {/* עיגול שחור עם חץ כתום - רק כשמסומן */}
                      {termsAccepted && (
                        <div 
                          className="flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-75 ease-out group-hover:-translate-x-2"
                          style={{
                            position: 'absolute',
                            left: '0px',
                            width: 'clamp(48px, 5.5vw, 58px)',
                            height: 'clamp(48px, 5.5vw, 58px)',
                            backgroundColor: '#000000',
                            border: '2px solid #D4943A',
                          }}
                        >
                          {/* אייקון חץ */}
                          <svg 
                            className="transition-transform duration-75 ease-out rotate-45 group-hover:!rotate-0"
                            width="28" 
                            height="28" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            xmlns="http://www.w3.org/2000/svg"
                            style={{
                              color: colors.gold.base,
                            }}
                          >
                            <path 
                              d="M19 12H5M5 12L12 5M5 12L12 19" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      )}
                    </button>
                  </div>
                </form>
                </div>
              </div>
            </div>
            
            {/* Age Restriction Notice - בתחתית העמוד, קטן מאוד */}
            <div 
              className="mt-12 text-center"
              style={{ 
                padding: 'clamp(12px, 2vw, 20px)',
              }}
            >
              <p 
                style={{ 
                  color: colors.textMuted,
                  fontSize: 'clamp(0.7rem, 1.5vw, 0.85rem)', // קטן מאוד
                  fontWeight: 400,
                  lineHeight: 1.4,
                  margin: 0,
                }}
              >
                ⚠️ רכישה באתר מותרת רק למי שמלאו לו 18 שנים ומעלה. הגבלה זו חלה על רכישות באשראי ולא על שימוש באתר עצמו.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      
      {/* Snackbar for error notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{
            width: '100%',
            direction: 'rtl',
            '& .MuiAlert-icon': {
              marginLeft: 1,
              marginRight: 0,
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default PaymentPage;
